import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ServicioSapRepository } from 'src/app/domain/service/servicio-sap/servicio-sap-repository';
import {
  ServicioSap,
  ServicioSapDireccion,
  ServicioSapFiltro,
  ServicioSapRequest,
  ServicioSapSearchResult
} from 'src/app/domain/model/servicio-sap/servicio-sap';
import { MAX_PAGE_SIZE } from 'src/app/common/constants-ejb';

/**
 * Adaptador HTTP: HttpServicioSapRepository
 *
 * Endpoints (sin prefijo de versión): `POST /buscar`, `POST /`, `PUT /{cdservicio}`,
 * `DELETE /{cdservicio}`.
 *
 * El código viaja codificado en la ruta y los filtros vacíos no se envían
 * (el servidor trata ausente = sin filtro).
 */
@Injectable({ providedIn: 'root' })
export class HttpServicioSapRepository implements ServicioSapRepository {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/servicios-sap`;
  private static readonly PAGE_SIZE_MIN = 1;

  async buscar(
    filtro: ServicioSapFiltro,
    size: number,
    cursor?: string | null,
    direction: ServicioSapDireccion = 'NEXT'
  ): Promise<ServicioSapSearchResult> {
    this.validarPaginacion(size);

    const body: Record<string, unknown> = { size, direction };
    const codigo = filtro.codigo?.trim();
    const descripcion = filtro.descripcion?.trim();
    if (codigo) body['codigo'] = codigo;
    if (descripcion) body['descripcion'] = descripcion;
    if (cursor) body['cursor'] = cursor;

    return firstValueFrom(
      this.http.post<ServicioSapSearchResult>(`${this.baseUrl}/buscar`, body)
    );
  }

  async crear(request: ServicioSapRequest): Promise<ServicioSap> {
    return firstValueFrom(this.http.post<ServicioSap>(this.baseUrl, request));
  }

  async actualizar(
    cdservicio: string,
    dsservicio: string
  ): Promise<ServicioSap> {
    return firstValueFrom(
      this.http.put<ServicioSap>(
        `${this.baseUrl}/${encodeURIComponent(cdservicio)}`,
        { dsservicio }
      )
    );
  }

  async eliminar(cdservicio: string): Promise<void> {
    await firstValueFrom(
      this.http.delete<void>(
        `${this.baseUrl}/${encodeURIComponent(cdservicio)}`
      )
    );
  }

  private validarPaginacion(size: number): void {
    if (
      !Number.isInteger(size) ||
      size < HttpServicioSapRepository.PAGE_SIZE_MIN ||
      size > MAX_PAGE_SIZE
    ) {
      throw new Error(
        `size debe estar entre ${HttpServicioSapRepository.PAGE_SIZE_MIN} y ${MAX_PAGE_SIZE}.`
      );
    }
  }
}
